import Backoffice from "@snark/backoffice";

const context = {
  account: Backoffice.populator("account", {}),
  building: Backoffice.populator("building", {}),
  user: Backoffice.populator("user", {}),
};

const PERMISSION_MODULES = [
  { value: "scenario", label: "Scenario" },
  { value: "agenda", label: "Agenda" },
  { value: "configuration", label: "Configuration" },
  { value: "decoupage", label: "Découpage" },
  { value: "store", label: "Store" },
  { value: "monitoring", label: "Monitoring" },
];

const PERMISSION_LEVELS = [
  { value: "none", label: "None" },
  { value: "view", label: "View" },
  { value: "edit", label: "Edit" },
];

function normalizePermissions(value) {
  const permissions = {};

  if (Array.isArray(value)) {
    value.forEach(permission => {
      if (permission?.module && permission?.level) {
        permissions[permission.module] = permission.level;
      }
    });
  } else if (value && typeof value === "object") {
    PERMISSION_MODULES.forEach(module => {
      permissions[module.value] = value[module.value] || "none";
    });
  }

  return permissions;
}

function normalizeBuildings(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map(building => building?.value ?? building?._id ?? building)
    .filter(Boolean);
}

export default {
  name: "group",
  collection: "group",
  idProperty: "_id",
  section: "app",
  backPopulateList: ["account"],

  labels: {
    singleTitle: "Group",
    listTitle: "Groups",
  },

  emptyItem: {
    name: "",
    accountId: null,
    permissions: {},
    buildings: [],
  },

  fields: [
    {
      name: "_id",
      label: "Id",
      readOnly: true,
      hideOnCreate: true,
    },

    {
      name: "accountId",
      label: "Account Id",
      formatter: Backoffice.formatters.linkObjectId("account", {
        idProperty: "_id",
        "label": context.account.formatter()
      }),
      editor: context.account.editor(),
    },

    {
      name: "name",
      label: "Group name",
      required: true,
      readOnly: true,
      formatter: (_field, item) => {
        if (!item?.name) return "-";
        if (!item.name.startsWith("__user__")) return item.name;
        const userId = item.name.replace("__user__", "");
        const user = (context.user.items || []).find(u => String(u._id) === String(userId));
        if (!user) return item.name;
        const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
        return fullName || user.email || item.name;
      },
    },

    {
      name: "permissions",
      label: "Permissions",
      list: true,
      formatter: (field, item) => {
        if (!item.permissions) return [];
        return Object.keys(item.permissions).map(module => ({
          module,
          level: item.permissions[module],
        }));
      },
      transform: (value, item) => {
        const permissions = {};
        (value || []).forEach(p => {
          if (p.module && p.level) {
            permissions[p.module] = p.level;
          }
        });
        item.permissions = permissions;
        return permissions;
      },
      children: PERMISSION_MODULES.map(module => ({
        name: module.value,
        label: module.label,
        editor: { type: "select", options: PERMISSION_LEVELS },
      })),
    },

    {
      name: "buildings",
      label: "Buildings",
      list: true,
      editor: {
        type: "checks",
        options: (field) => {
          const accountId = field.object.accountId;
          const filtered = (context.building.items || [])
            .filter(b => !accountId || String(b.accountId) === String(accountId))
            .map(b => ({ value: b._id, label: b.name }));

          return Promise.resolve(filtered);
        },
        watch: "accountId",
      },
      transform: (value, item) => {
        if (Array.isArray(value)) {
          item.buildings = value.map(v => v.value ?? v);
        } else if (!Array.isArray(item.buildings)) {
          item.buildings = [];
        }
        return item.buildings;
      }
    },

    {
      name: "createdAt",
      label: "Created at",
      readOnly: true,
      hideOnCreate: true,
      formatter: Backoffice.formatters.date("DD/MM/YYYY"),
    },

    {
      name: "updatedAt",
      label: "Updated at",
      readOnly: true,
      hideOnCreate: true,
      formatter: Backoffice.formatters.date("DD/MM/YYYY"),
    },
  ],

  listColumns: [
    {
      name: "name",
      label: "Name",
      formatter: (_field, item) => {
        if (!item?.name) return "-";
        if (!item.name.startsWith("__user__")) return item.name;
        const userId = item.name.replace("__user__", "");
        const user = (context.user.items || []).find(u => String(u._id) === String(userId));
        if (!user) return item.name;
        const fullName = `${user.firstname || ""} ${user.lastname || ""}`.trim();
        return fullName || user.email || item.name;
      },
    },
    {
      name: "account",
      label: "Account",
      formatter: (_field, item) => item.account?.name || item.accountId || "-",
      nosingle: true,
    },
    {
      name: "buildings",
      label: "Buildings",
      formatter: (_field, item) => {
        const ids = Array.isArray(item.buildings) ? item.buildings : [];
        const names = (context.building.items || [])
          .filter(b => ids.map(String).includes(String(b._id)))
          .map(b => b.name);
        return names.join(", ");
      },
      nosingle: true,
    },
  ],

  loadList() {
    return Promise.all([context.account.load(), context.building.load(), context.user.load()]);
  },

  loadSingle() {
    return Promise.all([context.account.load(), context.building.load(), context.user.load()]);
  },

  completeSingle(item) {
    item.permissions = normalizePermissions(item.permissions);
    item.buildings = normalizeBuildings(item.buildings);
    return item;
  },

  beforeSave(item) {
    return {
      ...item,
      permissions: normalizePermissions(item.permissions),
      buildings: normalizeBuildings(item.buildings),
    };
  },
};
